/** % of rows that must belong to the target month for an upload to be accepted. */
export const MONTH_VALIDATION_THRESHOLD_PCT = 70

export interface MonthValidationResult {
  valid: boolean
  matchedPct: number
  detectedMonths: { label: string; pct: number }[]
  reason?: string
}

const buildDetectedMonths = (labels: (string | null)[]): { label: string; pct: number }[] => {
  const counts = new Map<string, number>()
  let total = 0
  for (const label of labels) {
    if (!label) continue
    counts.set(label, (counts.get(label) ?? 0) + 1)
    total++
  }
  if (total === 0) return []
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, pct: (count / total) * 100 }))
    .sort((a, b) => b.pct - a.pct)
}

/** Checks whether uploaded dates primarily belong to the selected month. */
export function validateMonthMembership(
  dateStrings: string[],
  targetMonthLabel: string,
  getLabel: (dateStr: string) => string | null,
  thresholdPct: number = MONTH_VALIDATION_THRESHOLD_PCT
): MonthValidationResult {
  const detectedMonths = buildDetectedMonths(dateStrings.map(getLabel))

  if (detectedMonths.length === 0) {
    return { valid: false, matchedPct: 0, detectedMonths, reason: "No order dates found in the uploaded file." }
  }

  const matchedPct = detectedMonths.find(month => month.label === targetMonthLabel)?.pct ?? 0
  if (matchedPct >= thresholdPct) return { valid: true, matchedPct, detectedMonths }

  const dominant = detectedMonths.find(month => month.label !== targetMonthLabel) ?? detectedMonths[0]
  return {
    valid: false,
    matchedPct,
    detectedMonths,
    reason: `This upload contains ${dominant.label} data. Please upload ${targetMonthLabel} files.`
  }
}
